import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  LocalOffer,
  PlaylistAddCheck,
  SaveOutlined,
  Summarize,
  UploadFile,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';

import MainText from '../../components/MainText';
import API from '../../services/api';
import { addTab } from '../../tabs/tabsSlice';

const todayIso = () => new Date().toISOString().split('T')[0];

const tokenize = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

const getOrderCode = (order) => order?.public_code || order?.supplier_code || `OC #${order?.id || ''}`;

const supplierLabel = (option) => option?.companyName || option?.legalName || '';

const statusToChipColor = (status) => {
  if (status === 'completed') return 'success';
  if (status === 'partial') return 'warning';
  if (status === 'pending') return 'default';
  if (status === 'cancelled') return 'default';
  return 'default';
};

const buildOrderProductMap = (orders) => {
  const map = {};
  (orders || []).forEach((order) => {
    const byProduct = new Map();
    (order?.order_details || []).forEach((detail) => {
      const product = detail?.product;
      if (!product?.id) return;
      if (byProduct.has(product.id)) return;
      byProduct.set(product.id, {
        id: product.id,
        code: product.code || '',
        description: product.description || '',
        prformat_id: product?.prformat?.id || null,
      });
    });
    map[order.id] = Array.from(byProduct.values());
  });
  return map;
};

const emptyDocumentForm = () => ({
  supplier_id: null,
  number: '',
  series: '',
  date: todayIso(),
  total: '',
  comment: '',
  file: null,
  lines: [],
});

export default function SellOutPromotions() {
  const dispatch = useDispatch();

  const [tab, setTab] = useState(0);

  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  const [ordersBySupplier, setOrdersBySupplier] = useState({});
  const [orderProductsBySupplier, setOrderProductsBySupplier] = useState({});
  const [loadingOrders, setLoadingOrders] = useState(false);

  const [openOrderModal, setOpenOrderModal] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  const [promiseForm, setPromiseForm] = useState({
    supplier_id: null,
    date: todayIso(),
    comment: '',
    evidence: null,
    lines: [],
  });
  const [selectedPromiseRows, setSelectedPromiseRows] = useState([]);
  const [bulkPromoProductId, setBulkPromoProductId] = useState(null);
  const [savingPromise, setSavingPromise] = useState(false);

  const [documentStep, setDocumentStep] = useState(0);
  const [documentForm, setDocumentForm] = useState(emptyDocumentForm());
  const [loadingPending, setLoadingPending] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false);

  const [summaryFilters, setSummaryFilters] = useState({
    supplier_id: null,
    order_id: null,
  });
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    severity: 'info',
    message: '',
  });

  const showSnack = useCallback((severity, message) => {
    setSnack({ open: true, severity, message });
  }, []);

  useEffect(() => {
    dispatch(addTab({ label: 'Promociones Sell Out', path: '/sellout/promotions' }));
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingSuppliers(true);
      try {
        const { data } = await API.get('suppliers/minimal/', { params: { active: true } });
        if (!mounted) return;
        setSuppliers(Array.isArray(data) ? data : []);
      } catch {
        if (!mounted) return;
        setSuppliers([]);
        showSnack('error', 'No se pudo cargar el listado de proveedores.');
      } finally {
        if (mounted) setLoadingSuppliers(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [showSnack]);

  const ensureOrdersForSupplier = useCallback(async (supplierId) => {
    if (!supplierId) return [];
    if (Array.isArray(ordersBySupplier[supplierId])) {
      return ordersBySupplier[supplierId];
    }

    setLoadingOrders(true);
    try {
      const { data } = await API.get('order-Av/get_orders_with_availability/', {
        params: { supplier_id: supplierId },
      });
      const rows = Array.isArray(data) ? data : [];
      setOrdersBySupplier((prev) => ({
        ...prev,
        [supplierId]: rows,
      }));
      setOrderProductsBySupplier((prev) => ({
        ...prev,
        [supplierId]: buildOrderProductMap(rows),
      }));
      return rows;
    } catch {
      showSnack('error', 'No se pudieron cargar las órdenes del proveedor.');
      return [];
    } finally {
      setLoadingOrders(false);
    }
  }, [ordersBySupplier, showSnack]);

  const promiseSupplier = useMemo(
    () => suppliers.find((supplier) => supplier.id === promiseForm.supplier_id) || null,
    [promiseForm.supplier_id, suppliers]
  );

  const documentSupplier = useMemo(
    () => suppliers.find((supplier) => supplier.id === documentForm.supplier_id) || null,
    [documentForm.supplier_id, suppliers]
  );

  const summarySupplier = useMemo(
    () => suppliers.find((supplier) => supplier.id === summaryFilters.supplier_id) || null,
    [summaryFilters.supplier_id, suppliers]
  );

  const promiseOrders = useMemo(
    () => ordersBySupplier[promiseForm.supplier_id] || [],
    [ordersBySupplier, promiseForm.supplier_id]
  );

  const summaryOrders = useMemo(
    () => ordersBySupplier[summaryFilters.supplier_id] || [],
    [ordersBySupplier, summaryFilters.supplier_id]
  );

  const orderSearchTokens = useMemo(() => tokenize(orderSearch), [orderSearch]);

  const filteredOrders = useMemo(() => {
    if (!orderSearchTokens.length) return promiseOrders;
    return promiseOrders.filter((order) => {
      const detailsText = (order?.order_details || [])
        .map((detail) => `${detail?.product?.code || ''} ${detail?.product?.description || ''}`)
        .join(' ')
        .toLowerCase();
      const haystack = [
        String(order?.id || ''),
        String(order?.public_code || ''),
        String(order?.supplier_code || ''),
        String(order?.orderStatus || ''),
        detailsText,
      ]
        .join(' ')
        .toLowerCase();
      return orderSearchTokens.every((token) => haystack.includes(token));
    });
  }, [orderSearchTokens, promiseOrders]);

  const orderRows = useMemo(
    () =>
      filteredOrders.map((order) => ({
        id: order.id,
        code: getOrderCode(order),
        status: order.orderStatus || '-',
        details: (order?.order_details || []).length,
        total_qty: (order?.order_details || []).reduce(
          (acc, detail) => acc + Number(detail?.quantity || 0),
          0
        ),
      })),
    [filteredOrders]
  );

  const orderColumns = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 90 },
      { field: 'code', headerName: 'Código orden', flex: 1, minWidth: 170 },
      { field: 'status', headerName: 'Estado', width: 130 },
      { field: 'details', headerName: '# Líneas', width: 110 },
      { field: 'total_qty', headerName: 'Cant. total', width: 120 },
    ],
    []
  );

  const getOrderProductOptions = useCallback(
    (supplierId, orderId) => {
      const map = orderProductsBySupplier[supplierId] || {};
      return map[orderId] || [];
    },
    [orderProductsBySupplier]
  );

  const openOrderPicker = async () => {
    if (!promiseForm.supplier_id) {
      showSnack('warning', 'Selecciona un proveedor antes de cargar productos desde orden.');
      return;
    }
    await ensureOrdersForSupplier(promiseForm.supplier_id);
    setSelectedOrderIds([]);
    setOrderSearch('');
    setOpenOrderModal(true);
  };

  const loadPromiseLinesFromSelectedOrders = () => {
    if (!selectedOrderIds.length) {
      showSnack('warning', 'Selecciona al menos una orden para cargar productos.');
      return;
    }

    const existingByKey = new Map((promiseForm.lines || []).map((line) => [line.key, line]));
    const selected = promiseOrders.filter((order) => selectedOrderIds.includes(order.id));

    const nextLines = [];
    selected.forEach((order) => {
      (order?.order_details || []).forEach((detail) => {
        const product = detail?.product;
        if (!product?.id || !detail?.id) return;

        const key = `${order.id}-${detail.id}`;
        const orderedQty = Number(detail?.quantity || 0);
        const defaultPromised = Number(detail?.remaining_quantity || orderedQty || 0);
        const existing = existingByKey.get(key);

        nextLines.push({
          key,
          order_id: order.id,
          order_code: getOrderCode(order),
          order_detail_id: detail.id,
          base_product_id: product.id,
          base_product_label: `${product.code || ''} ${product.description || ''}`.trim(),
          ordered_qty: orderedQty,
          promised_qty: existing ? Number(existing.promised_qty || 0) : defaultPromised,
          promo_product_id: existing?.promo_product_id || null,
          promo_format_id: existing?.promo_format_id || null,
          comment: existing?.comment || '',
        });
      });
    });

    setPromiseForm((prev) => ({ ...prev, lines: nextLines }));
    setSelectedPromiseRows([]);
    setBulkPromoProductId(null);
    setOpenOrderModal(false);
  };

  const updatePromiseLine = (key, field, value) => {
    setPromiseForm((prev) => ({
      ...prev,
      lines: (prev.lines || []).map((line) => (line.key === key ? { ...line, [field]: value } : line)),
    }));
  };

  const promiseBulkPromoOptions = useMemo(() => {
    if (!selectedPromiseRows.length) return [];
    const byId = new Map();
    selectedPromiseRows.forEach((key) => {
      const line = promiseForm.lines.find((row) => row.key === key);
      if (!line) return;
      const options = getOrderProductOptions(promiseForm.supplier_id, line.order_id);
      options.forEach((option) => {
        if (!byId.has(option.id)) byId.set(option.id, option);
      });
    });
    return Array.from(byId.values());
  }, [getOrderProductOptions, promiseForm.lines, promiseForm.supplier_id, selectedPromiseRows]);

  const applyBulkPromoProduct = () => {
    if (!selectedPromiseRows.length) {
      showSnack('warning', 'Selecciona líneas para aplicar el promocional en lote.');
      return;
    }
    const selectedPromo = promiseBulkPromoOptions.find((option) => option.id === bulkPromoProductId) || null;
    setPromiseForm((prev) => ({
      ...prev,
      lines: (prev.lines || []).map((line) => {
        if (!selectedPromiseRows.includes(line.key)) return line;
        return {
          ...line,
          promo_product_id: selectedPromo?.id || null,
          promo_format_id: selectedPromo?.prformat_id || null,
        };
      }),
    }));
  };

  const handleSavePromise = async () => {
    if (!promiseForm.supplier_id) {
      showSnack('warning', 'Selecciona un proveedor para registrar la promesa.');
      return;
    }
    if (!promiseForm.lines.length) {
      showSnack('warning', 'Primero carga productos desde una o más órdenes.');
      return;
    }

    for (const line of promiseForm.lines) {
      if (!line.order_id || !line.base_product_id) {
        showSnack('warning', 'Todas las líneas deben tener orden y producto base válido.');
        return;
      }
      if (Number(line.promised_qty || 0) <= 0) {
        showSnack('warning', 'La cantidad prometida debe ser mayor a cero en todas las líneas.');
        return;
      }
    }

    setSavingPromise(true);
    try {
      const payload = {
        supplier_id: promiseForm.supplier_id,
        date: promiseForm.date || todayIso(),
        comment: promiseForm.comment || '',
        lines_payload: promiseForm.lines.map((line) => ({
          order_id: line.order_id,
          order_detail_id: line.order_detail_id || null,
          base_product_id: line.base_product_id,
          promo_product_id: line.promo_product_id || null,
          promo_format_id: line.promo_format_id || null,
          promised_qty: Number(line.promised_qty || 0),
          comment: line.comment || '',
        })),
      };

      const { data } = await API.post('sellout/commitments/', payload);
      const createdId = data?.id;

      if (createdId && promiseForm.evidence instanceof File) {
        const fd = new FormData();
        fd.append('evidence', promiseForm.evidence);
        await API.patch(`sellout/commitments/${createdId}/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      showSnack('success', 'Promesa Sell Out registrada correctamente.');
      setPromiseForm((prev) => ({
        supplier_id: prev.supplier_id,
        date: todayIso(),
        comment: '',
        evidence: null,
        lines: [],
      }));
      setSummaryFilters((prev) => ({ ...prev, supplier_id: promiseForm.supplier_id }));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      showSnack('error', detail || 'No fue posible registrar la promesa Sell Out.');
    } finally {
      setSavingPromise(false);
    }
  };

  const loadPendingCommitmentLines = useCallback(async () => {
    if (!documentForm.supplier_id) {
      showSnack('warning', 'Selecciona un proveedor en el paso 1.');
      return;
    }

    await ensureOrdersForSupplier(documentForm.supplier_id);
    setLoadingPending(true);
    try {
      const { data } = await API.get('sellout/commitment-lines/pending-by-supplier/', {
        params: { supplier_id: documentForm.supplier_id },
      });
      const pending = Array.isArray(data) ? data : [];

      const mapped = pending.map((line) => {
        const orderId = line?.order?.id;
        const options = getOrderProductOptions(documentForm.supplier_id, orderId);
        const defaultRealProductId =
          options.find((option) => option.id === line?.promo_product?.id)?.id
          || options.find((option) => option.id === line?.base_product?.id)?.id
          || null;
        const defaultRealProduct = options.find((option) => option.id === defaultRealProductId) || null;

        return {
          key: String(line.id),
          sellout_commitment_line_id: line.id,
          order_id: orderId,
          base_product_label: `${line?.base_product?.code || ''} ${line?.base_product?.description || ''}`.trim(),
          promo_product_label: line?.promo_product
            ? `${line?.promo_product?.code || ''} ${line?.promo_product?.description || ''}`.trim()
            : 'Sin promocional asignado',
          promised_qty: Number(line?.promised_qty || 0),
          historical_received: Number(line?.total_received || 0),
          historical_rejected: Number(line?.total_rejected || 0),
          pending_qty: Number(line?.total_missing || 0),
          received_qty: '',
          rejected_qty: '',
          real_product_id: defaultRealProduct?.id || null,
          real_format_id: defaultRealProduct?.prformat_id || null,
          comment: '',
        };
      });

      setDocumentForm((prev) => ({ ...prev, lines: mapped }));
      showSnack('info', mapped.length ? 'Promesas pendientes cargadas.' : 'No hay promesas pendientes para este proveedor.');
    } catch {
      showSnack('error', 'No fue posible cargar promesas pendientes.');
    } finally {
      setLoadingPending(false);
    }
  }, [documentForm.supplier_id, ensureOrdersForSupplier, getOrderProductOptions, showSnack]);

  const updateDocumentLine = (key, field, value) => {
    setDocumentForm((prev) => ({
      ...prev,
      lines: (prev.lines || []).map((line) => (line.key === key ? { ...line, [field]: value } : line)),
    }));
  };

  const documentStep1Valid = useMemo(
    () => Boolean(documentForm.supplier_id && String(documentForm.number || '').trim() && documentForm.date),
    [documentForm.date, documentForm.number, documentForm.supplier_id]
  );

  const documentStep2Valid = useMemo(() => {
    if (!Array.isArray(documentForm.lines) || documentForm.lines.length === 0) return false;
    let hasAnyReceived = false;
    for (const line of documentForm.lines) {
      const received = Number(line.received_qty || 0);
      const rejected = Number(line.rejected_qty || 0);
      if (Number.isNaN(received) || Number.isNaN(rejected) || received < 0 || rejected < 0) return false;
      if (rejected > received) return false;
      if (received > 0) hasAnyReceived = true;
    }
    return hasAnyReceived;
  }, [documentForm.lines]);

  const documentTotals = useMemo(() => {
    const received = (documentForm.lines || []).reduce((acc, line) => acc + Number(line.received_qty || 0), 0);
    const rejected = (documentForm.lines || []).reduce((acc, line) => acc + Number(line.rejected_qty || 0), 0);
    const accepted = Math.max(received - rejected, 0);
    return { received, rejected, accepted };
  }, [documentForm.lines]);

  const handleSaveDocument = async () => {
    if (!documentStep1Valid) {
      showSnack('warning', 'Completa proveedor, número y fecha del documento.');
      return;
    }
    if (!documentStep2Valid) {
      showSnack('warning', 'Revisa las líneas: debe existir al menos un recibido y rechazado no puede exceder recibido.');
      return;
    }

    setSavingDocument(true);
    try {
      const payload = {
        supplier_id: documentForm.supplier_id,
        number: String(documentForm.number || '').trim(),
        series: String(documentForm.series || '').trim(),
        date: documentForm.date,
        total: documentForm.total === '' ? null : Number(documentForm.total || 0),
        comment: documentForm.comment || '',
        lines_payload: (documentForm.lines || []).map((line) => {
          const received = Number(line.received_qty || 0);
          const rejected = Number(line.rejected_qty || 0);
          const accepted = Math.max(received - rejected, 0);
          return {
            sellout_commitment_line_id: line.sellout_commitment_line_id,
            real_product_id: line.real_product_id || null,
            real_format_id: line.real_format_id || null,
            received_qty: received,
            accepted_qty: accepted,
            rejected_qty: rejected,
            comment: line.comment || '',
          };
        }),
      };

      const { data } = await API.post('sellout/documents/', payload);
      const createdId = data?.id;

      if (createdId && documentForm.file instanceof File) {
        const fd = new FormData();
        fd.append('file', documentForm.file);
        await API.patch(`sellout/documents/${createdId}/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      showSnack('success', 'Documento promocional registrado correctamente.');
      setSummaryFilters((prev) => ({ ...prev, supplier_id: documentForm.supplier_id }));
      setDocumentForm((prev) => ({ ...emptyDocumentForm(), supplier_id: prev.supplier_id }));
      setDocumentStep(0);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      showSnack('error', detail || 'No fue posible registrar el documento promocional.');
    } finally {
      setSavingDocument(false);
    }
  };

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const params = {};
      if (summaryFilters.supplier_id) params.supplier_id = summaryFilters.supplier_id;
      if (summaryFilters.order_id) params.order_id = summaryFilters.order_id;

      const { data } = await API.get('sellout/commitment-lines/', { params });
      setSummaryRows(Array.isArray(data) ? data : []);
    } catch {
      setSummaryRows([]);
      showSnack('error', 'No se pudo cargar el resumen de cumplimiento Sell Out.');
    } finally {
      setSummaryLoading(false);
    }
  }, [showSnack, summaryFilters.order_id, summaryFilters.supplier_id]);

  useEffect(() => {
    if (tab !== 2) return;
    loadSummary();
  }, [tab, loadSummary]);

  const renderPromiseSection = () => (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Registrar promesa comercial
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={suppliers}
              loading={loadingSuppliers}
              value={promiseSupplier}
              getOptionLabel={supplierLabel}
              onChange={async (_, nextSupplier) => {
                const supplierId = nextSupplier?.id || null;
                setPromiseForm({
                  supplier_id: supplierId,
                  date: todayIso(),
                  comment: '',
                  evidence: null,
                  lines: [],
                });
                setSelectedPromiseRows([]);
                setBulkPromoProductId(null);
                if (supplierId) {
                  await ensureOrdersForSupplier(supplierId);
                }
              }}
              renderInput={(params) => <TextField {...params} label="Proveedor" size="small" />}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              label="Fecha"
              type="date"
              size="small"
              fullWidth
              value={promiseForm.date}
              onChange={(e) => setPromiseForm((prev) => ({ ...prev, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFile />}
              fullWidth
              sx={{ height: 40 }}
            >
              {promiseForm.evidence ? 'Cambiar evidencia' : 'Adjuntar evidencia'}
              <input
                type="file"
                hidden
                onChange={(e) =>
                  setPromiseForm((prev) => ({
                    ...prev,
                    evidence: e.target.files?.[0] || null,
                  }))
                }
              />
            </Button>
            {promiseForm.evidence && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                {promiseForm.evidence.name}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Comentario"
              size="small"
              fullWidth
              value={promiseForm.comment}
              onChange={(e) => setPromiseForm((prev) => ({ ...prev, comment: e.target.value }))}
            />
          </Grid>
        </Grid>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 2, mb: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<PlaylistAddCheck />}
            onClick={openOrderPicker}
            disabled={loadingOrders}
          >
            Cargar productos desde orden
          </Button>

          <Autocomplete
            options={promiseBulkPromoOptions}
            value={promiseBulkPromoOptions.find((option) => option.id === bulkPromoProductId) || null}
            getOptionLabel={(option) => `${option.code || ''} ${option.description || ''}`.trim()}
            onChange={(_, next) => setBulkPromoProductId(next?.id || null)}
            sx={{ minWidth: 320 }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Promocional en lote (líneas seleccionadas)" />
            )}
          />

          <Button
            variant="text"
            onClick={applyBulkPromoProduct}
            disabled={!selectedPromiseRows.length}
          >
            Aplicar promocional
          </Button>
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 940 }}>
            <Box component="thead">
              <Box component="tr" sx={{ bgcolor: 'grey.100' }}>
                {['', 'Orden', 'Producto base', 'Cantidad orden', 'Cantidad prometida', 'Promocional', 'Comentario'].map((label) => (
                  <Box key={label} component="th" sx={{ textAlign: 'left', p: 1, fontSize: 13 }}>
                    {label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {(promiseForm.lines || []).map((line) => {
                const promoOptions = getOrderProductOptions(promiseForm.supplier_id, line.order_id);
                const selectedPromo = promoOptions.find((option) => option.id === line.promo_product_id) || null;
                return (
                  <Box component="tr" key={line.key}>
                    <Box component="td" sx={{ p: 1 }}>
                      <Checkbox
                        size="small"
                        checked={selectedPromiseRows.includes(line.key)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedPromiseRows((prev) => {
                            if (checked) return [...prev, line.key];
                            return prev.filter((value) => value !== line.key);
                          });
                        }}
                      />
                    </Box>
                    <Box component="td" sx={{ p: 1 }}>{line.order_code}</Box>
                    <Box component="td" sx={{ p: 1 }}>{line.base_product_label}</Box>
                    <Box component="td" sx={{ p: 1 }}>{line.ordered_qty}</Box>
                    <Box component="td" sx={{ p: 1, width: 170 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={line.promised_qty}
                        inputProps={{ min: 1 }}
                        onChange={(e) => {
                          const value = Math.max(Number(e.target.value || 0), 0);
                          updatePromiseLine(line.key, 'promised_qty', value);
                        }}
                      />
                    </Box>
                    <Box component="td" sx={{ p: 1, minWidth: 260 }}>
                      <Autocomplete
                        options={promoOptions}
                        value={selectedPromo}
                        getOptionLabel={(option) => `${option.code || ''} ${option.description || ''}`.trim()}
                        onChange={(_, next) => {
                          updatePromiseLine(line.key, 'promo_product_id', next?.id || null);
                          updatePromiseLine(line.key, 'promo_format_id', next?.prformat_id || null);
                        }}
                        renderInput={(params) => <TextField {...params} size="small" label="Promocional" />}
                      />
                    </Box>
                    <Box component="td" sx={{ p: 1, minWidth: 240 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={line.comment}
                        onChange={(e) => updatePromiseLine(line.key, 'comment', e.target.value)}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveOutlined />}
            onClick={handleSavePromise}
            disabled={savingPromise}
          >
            Guardar promesa
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderDocumentSection = () => {
    const stepLabels = ['Documento', 'Detalle recibido', 'Revisión'];

    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Registrar documento promocional separado
          </Typography>

          <Stepper activeStep={documentStep} sx={{ mb: 3 }}>
            {stepLabels.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {documentStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={suppliers}
                  loading={loadingSuppliers}
                  value={documentSupplier}
                  getOptionLabel={supplierLabel}
                  onChange={async (_, nextSupplier) => {
                    const supplierId = nextSupplier?.id || null;
                    setDocumentForm((prev) => ({ ...emptyDocumentForm(), supplier_id: supplierId }));
                    if (supplierId) {
                      await ensureOrdersForSupplier(supplierId);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Proveedor" size="small" />}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  size="small"
                  label="Número"
                  value={documentForm.number}
                  onChange={(e) => setDocumentForm((prev) => ({ ...prev, number: e.target.value }))}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  size="small"
                  label="Serie"
                  value={documentForm.series}
                  onChange={(e) => setDocumentForm((prev) => ({ ...prev, series: e.target.value.toUpperCase() }))}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  label="Fecha"
                  type="date"
                  size="small"
                  fullWidth
                  value={documentForm.date}
                  onChange={(e) => setDocumentForm((prev) => ({ ...prev, date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  size="small"
                  type="number"
                  label="Total (opcional)"
                  value={documentForm.total}
                  onChange={(e) => setDocumentForm((prev) => ({ ...prev, total: e.target.value }))}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  size="small"
                  label="Comentario"
                  value={documentForm.comment}
                  onChange={(e) => setDocumentForm((prev) => ({ ...prev, comment: e.target.value }))}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFile />}
                  fullWidth
                  sx={{ height: 40 }}
                >
                  {documentForm.file ? 'Cambiar archivo' : 'Adjuntar documento'}
                  <input
                    type="file"
                    hidden
                    onChange={(e) =>
                      setDocumentForm((prev) => ({
                        ...prev,
                        file: e.target.files?.[0] || null,
                      }))
                    }
                  />
                </Button>
                {documentForm.file && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    {documentForm.file.name}
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}

          {documentStep === 1 && (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<PlaylistAddCheck />}
                  onClick={loadPendingCommitmentLines}
                  disabled={loadingPending}
                >
                  Cargar promesas pendientes
                </Button>
              </Stack>

              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 1080 }}>
                  <Box component="thead">
                    <Box component="tr" sx={{ bgcolor: 'grey.100' }}>
                      {['Producto base', 'Promocional', 'Prometido', 'Recibido', 'Rechazado', 'Pendiente', 'Producto real', 'Comentario'].map((label) => (
                        <Box key={label} component="th" sx={{ textAlign: 'left', p: 1, fontSize: 13 }}>
                          {label}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {(documentForm.lines || []).map((line) => {
                      const productOptions = getOrderProductOptions(documentForm.supplier_id, line.order_id);
                      const selectedRealProduct =
                        productOptions.find((option) => option.id === line.real_product_id) || null;
                      return (
                        <Box component="tr" key={line.key}>
                          <Box component="td" sx={{ p: 1 }}>{line.base_product_label}</Box>
                          <Box component="td" sx={{ p: 1 }}>{line.promo_product_label}</Box>
                          <Box component="td" sx={{ p: 1 }}>{line.promised_qty}</Box>
                          <Box component="td" sx={{ p: 1, minWidth: 130 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={line.received_qty}
                              onChange={(e) => {
                                const value = Math.max(Number(e.target.value || 0), 0);
                                updateDocumentLine(line.key, 'received_qty', value);
                              }}
                              fullWidth
                              inputProps={{ min: 0 }}
                            />
                          </Box>
                          <Box component="td" sx={{ p: 1, minWidth: 130 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={line.rejected_qty}
                              onChange={(e) => {
                                const value = Math.max(Number(e.target.value || 0), 0);
                                updateDocumentLine(line.key, 'rejected_qty', value);
                              }}
                              fullWidth
                              inputProps={{ min: 0 }}
                            />
                          </Box>
                          <Box component="td" sx={{ p: 1 }}>
                            <Tooltip title={`Recibido acumulado: ${line.historical_received} · Rechazado acumulado: ${line.historical_rejected}`}>
                              <Chip size="small" label={line.pending_qty} color="info" variant="outlined" />
                            </Tooltip>
                          </Box>
                          <Box component="td" sx={{ p: 1, minWidth: 280 }}>
                            <Autocomplete
                              options={productOptions}
                              value={selectedRealProduct}
                              getOptionLabel={(option) => `${option.code || ''} ${option.description || ''}`.trim()}
                              onChange={(_, next) => {
                                updateDocumentLine(line.key, 'real_product_id', next?.id || null);
                                updateDocumentLine(line.key, 'real_format_id', next?.prformat_id || null);
                              }}
                              renderInput={(params) => <TextField {...params} size="small" label="Producto real" />}
                            />
                          </Box>
                          <Box component="td" sx={{ p: 1, minWidth: 220 }}>
                            <TextField
                              size="small"
                              fullWidth
                              value={line.comment}
                              onChange={(e) => updateDocumentLine(line.key, 'comment', e.target.value)}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {documentStep === 2 && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Resumen del documento promocional
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="small" label={`Líneas: ${documentForm.lines.length}`} />
                <Chip size="small" label={`Recibido: ${documentTotals.received}`} />
                <Chip size="small" label={`Aceptado: ${documentTotals.accepted}`} color="success" variant="outlined" />
                <Chip size="small" label={`Rechazado: ${documentTotals.rejected}`} color="warning" variant="outlined" />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Se registrará una factura promocional separada con sus líneas y cantidades recibidas/rechazadas.
              </Typography>
            </Stack>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setDocumentStep((prev) => Math.max(prev - 1, 0))}
              disabled={documentStep === 0}
            >
              Atrás
            </Button>

            {documentStep < 2 && (
              <Button
                variant="contained"
                onClick={() => {
                  if (documentStep === 0 && !documentStep1Valid) {
                    showSnack('warning', 'Completa proveedor, número y fecha para avanzar.');
                    return;
                  }
                  if (documentStep === 1 && !documentStep2Valid) {
                    showSnack('warning', 'Revisa detalle recibido/rechazado antes de avanzar.');
                    return;
                  }
                  setDocumentStep((prev) => Math.min(prev + 1, 2));
                }}
              >
                Siguiente
              </Button>
            )}

            {documentStep === 2 && (
              <Button
                variant="contained"
                startIcon={<SaveOutlined />}
                onClick={handleSaveDocument}
                disabled={savingDocument}
              >
                Guardar documento
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderSummarySection = () => (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 2 }}
        >
          <Autocomplete
            options={suppliers}
            value={summarySupplier}
            getOptionLabel={supplierLabel}
            onChange={async (_, nextSupplier) => {
              const supplierId = nextSupplier?.id || null;
              setSummaryFilters({ supplier_id: supplierId, order_id: null });
              if (supplierId) await ensureOrdersForSupplier(supplierId);
            }}
            sx={{ minWidth: 280 }}
            renderInput={(params) => <TextField {...params} label="Proveedor" size="small" />}
          />

          <TextField
            select
            size="small"
            label="Orden"
            sx={{ minWidth: 240 }}
            value={summaryFilters.order_id || ''}
            onChange={(e) =>
              setSummaryFilters((prev) => ({
                ...prev,
                order_id: e.target.value ? Number(e.target.value) : null,
              }))
            }
          >
            <MenuItem value="">Todas</MenuItem>
            {summaryOrders.map((order) => (
              <MenuItem key={order.id} value={order.id}>
                {getOrderCode(order)}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="outlined"
            startIcon={<Summarize />}
            onClick={loadSummary}
            disabled={summaryLoading}
          >
            Actualizar resumen
          </Button>
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <Box component="thead">
              <Box component="tr" sx={{ bgcolor: 'grey.100' }}>
                {['Producto', 'Prometido', 'Recibido', 'Rechazado', 'Faltante', 'Estado'].map((label) => (
                  <Box key={label} component="th" sx={{ textAlign: 'left', p: 1, fontSize: 13 }}>
                    {label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {(summaryRows || []).map((line) => (
                <Box component="tr" key={`summary-line-${line.id}`}>
                  <Box component="td" sx={{ p: 1 }}>
                    {line?.base_product?.description || '-'}
                    {line?.promo_product?.description ? (
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.75 }}>
                        → {line.promo_product.description}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box component="td" sx={{ p: 1 }}>{line.promised_qty}</Box>
                  <Box component="td" sx={{ p: 1 }}>{line.total_received}</Box>
                  <Box component="td" sx={{ p: 1 }}>{line.total_rejected}</Box>
                  <Box component="td" sx={{ p: 1 }}>{line.total_missing}</Box>
                  <Box component="td" sx={{ p: 1 }}>
                    <Chip size="small" label={line.status} color={statusToChipColor(line.status)} variant="outlined" />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <MainText
        title="Promociones Sell Out"
        subtitle="Promesa comercial, documento promocional separado y resumen de cumplimiento."
        icon={LocalOffer}
      />

      <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1 }}
        >
          <Tab label="Registrar promesa" />
          <Tab label="Registrar documento" />
          <Tab label="Resumen" />
        </Tabs>
      </Card>

      <Box sx={{ mt: 2 }}>
        {tab === 0 && renderPromiseSection()}
        {tab === 1 && renderDocumentSection()}
        {tab === 2 && renderSummarySection()}
      </Box>

      <Dialog open={openOrderModal} onClose={() => setOpenOrderModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Seleccionar órdenes</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              size="small"
              label="Buscar orden (tokens)"
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
            />
            <Box sx={{ height: 420, width: '100%' }}>
              <DataGrid
                rows={orderRows}
                columns={orderColumns}
                loading={loadingOrders}
                checkboxSelection
                disableColumnFilter
                disableColumnMenu
                rowSelectionModel={selectedOrderIds}
                onRowSelectionModelChange={(next) => setSelectedOrderIds(next)}
                pageSizeOptions={[10, 20, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOrderModal(false)}>Cancelar</Button>
          <Button variant="contained" onClick={loadPromiseLinesFromSelectedOrders}>
            Cargar productos
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
